import { ALL_CADENCES, CLOCK_CADENCE } from "./clock-cadences";

// A callback function that will be called when the wall clock ticks.
type Listener = () => void;

/**
 * Module-level singleton — shared across the entire app. Tracks listeners for each cadence; the tick
 * time itself is derived, not stored.
 */
const listeners = new Map<CLOCK_CADENCE, Set<Listener>>(ALL_CADENCES.map((c) => [c, new Set()]));

type ActivityHandler = (cadence: CLOCK_CADENCE) => void;

// A stack, because providers can overlap while one replaces another. The newest gets the
// notifications, but the others keep the timers they already armed, so an overlap ticks once per
// provider until one unmounts. Each is told which cadence changed, never what to do about it.
const activityHandlers: ActivityHandler[] = [];

function notifyCadenceActivity(cadence: CLOCK_CADENCE): void {
    activityHandlers[activityHandlers.length - 1]?.(cadence);
}

// Should always be exactly 1; used only in dev to catch accidental duplicate providers.
// HMR note: module scope persists across fast-refresh cycles, so this may read > 1
// spuriously after a hot-reload that unmounts and remounts the provider.
let providerMountCount = 0;

function listenersFor(cadence: CLOCK_CADENCE): Set<Listener> {
    const cadenceListeners = listeners.get(cadence);
    if (cadenceListeners === undefined)
        throw new Error(`WallClockService: unknown cadence ${cadence}. Add it to CLOCK_CADENCE and ALL_CADENCES.`);
    return cadenceListeners;
}

// The clock is shared, so one callback throwing must not cost the rest their tick.
export function runWallClockCallback(callback: Listener): void {
    try {
        callback();
    } catch (error) {
        console.error("WallClockService: a listener threw", error);
    }
}

/**
 * Pub/sub for wall clock ticks. Does not schedule timers — WallClockProvider calls `tick` fn.
 */
export const wallClockService = {
    // Notifies this cadence's listeners — WallClockProvider calls it on each interval, which is what
    // re-renders subscribed hooks.
    tick(cadence: CLOCK_CADENCE): void {
        listenersFor(cadence).forEach(runWallClockCallback);
    },
    /** Subscribe; returns unsubscribe. The first listener starts the cadence, the last one to go stops it. */
    subscribe(cadence: CLOCK_CADENCE, listener: Listener): () => void {
        const listenerSet = listenersFor(cadence);
        const wasIdle = listenerSet.size === 0;
        listenerSet.add(listener);
        if (wasIdle) notifyCadenceActivity(cadence);
        return () => {
            listenerSet.delete(listener);
            if (listenerSet.size === 0) notifyCadenceActivity(cadence);
        };
    },
    hasListeners(cadence: CLOCK_CADENCE): boolean {
        return listenersFor(cadence).size > 0;
    },
    /** Returns an unregister that hands scheduling back to whoever is left, rather than to nobody. */
    setCadenceActivityHandler(handler: ActivityHandler): () => void {
        activityHandlers.push(handler);
        return () => {
            const index = activityHandlers.indexOf(handler);
            if (index === -1) return;
            activityHandlers.splice(index, 1);
            // Whoever is left was shadowed through every transition since it registered, so it catches up.
            ALL_CADENCES.forEach(notifyCadenceActivity);
        };
    },
    // The cadence boundary the clock last passed (external-store snapshot). Derived rather than stored,
    // so a cadence nobody is watching cannot report a stale time.
    getTickTime(cadence: CLOCK_CADENCE): number {
        return Math.floor(Date.now() / cadence) * cadence;
    },
    // Provider lifecycle — used for dev duplicate-provider warning.
    notifyMounted(): void {
        providerMountCount++;
        if (process.env.NODE_ENV !== "production" && providerMountCount > 1) {
            console.warn(
                `WallClockService: ${providerMountCount} instances detected. Only one service should be mounted at a time — each provider arms its own timers, so every cadence ticks once per provider.`,
            );
        }
    },
    notifyUnmounted(): void {
        providerMountCount = Math.max(0, providerMountCount - 1);
    },
};
